export interface User {
  id: string;
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  password?: string;
  phone?: string;
  interests?: string[];
  location?: string;
  points?: number;
  level?: number;
  avatar: string;
  preferences?: {
    email?: boolean;
    sms?: boolean;
    weekly?: boolean;
    push?: boolean;
  };
  userType: 'user' | 'partner' | 'subpartner';
  isActive: boolean;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
  google?: {
    id?: string;
    accessToken?: string;
    refreshToken?: string;
    displayName?: string;
    email?: string;
    photo?: string;
  };
}

export enum BusinessType {
  Restaurant = 'restaurant',
  Market = 'market',
  Cafe = 'cafe',
  Retail = 'retail',
  Service = 'service',
  Technology = 'technology',
  Healthcare = 'healthcare',
  Education = 'education',
  Finance = 'finance',
  Manufacturing = 'manufacturing',
  Other = 'other'
}

export interface Business {
  _id: string;
  name: string;
  email: string;
  emailVerified?: boolean;
  password?: string;
  phone?: string;
  profilePhoto?: string;
  category: BusinessType;
  location?: {
    address?: string;
    city?: string;
    country?: string;
  };
  website?: string;
  description?: string;
  packageId?: string;
  google?: {
    id?: string;
    accessToken?: string;
    refreshToken?: string;
    displayName?: string;
    email?: string;
    photo?: string;
  };
  googleBusiness?: {
    accountId?: string;
    accountName?: string;
    locationId?: string;
    locationTitle?: string;
    mapsUrl?: string;
    placeId?: string;
    address?: string;
    phone?: string;
    website?: string;
    hours?: string;
    description?: string;
    category?: string;
  };
  facebook?: {
    username?: string;
    pageId?: string;
    accessToken?: string;
    refreshToken?: string;
    expiresAt?: Date;
    isConnected?: boolean;
    connectedAt?: Date;
    syncStatus?: 'pending' | 'completed' | 'failed';
    syncAt?: Date;
    syncError?: string;
  };
  instagram?: {
    username?: string;
    accountId?: string;
    accessToken?: string;
    refreshToken?: string;
    expiresAt?: Date;
    isConnected?: boolean;
    connectedAt?: Date;
    syncStatus?: 'pending' | 'completed' | 'failed';
    syncAt?: Date;
    syncError?: string;
  };
  twitter?: {
    username?: string;
    userId?: string;
    accessToken?: string;
    refreshToken?: string;
    expiresAt?: Date;
    isConnected?: boolean;
    connectedAt?: Date;
    syncStatus?: 'pending' | 'completed' | 'failed';
    syncAt?: Date;
    syncError?: string;
  };
  tiktok?: {
    username?: string;
    userId?: string;
    accessToken?: string;
    refreshToken?: string;
    expiresAt?: Date;
    isConnected?: boolean;
    connectedAt?: Date;
    syncStatus?: 'pending' | 'completed' | 'failed';
    syncAt?: Date;
    syncError?: string;
  };
  linkedin?: {
    username?: string;
    profileId?: string;
    accessToken?: string;
    refreshToken?: string;
    expiresAt?: Date;
    isConnected?: boolean;
    connectedAt?: Date;
    syncStatus?: 'pending' | 'completed' | 'failed';
    syncAt?: Date;
    syncError?: string;
  };
  analysisStatus?: {
    'instagram-analytics'?: {
      status?: 'noanalyze' | 'pending' | 'completed' | 'failed';
      lastAnalysisAt?: Date;
    };
    'facebook-analytics'?: {
      status?: 'noanalyze' | 'pending' | 'completed' | 'failed';
      lastAnalysisAt?: Date;
    };
    'twitter-analytics'?: {
      status?: 'noanalyze' | 'pending' | 'completed' | 'failed';
      lastAnalysisAt?: Date;
    };
    'linkedin-analytics'?: {
      status?: 'noanalyze' | 'pending' | 'completed' | 'failed';
      lastAnalysisAt?: Date;
    };
    'tiktok-analytics'?: {
      status?: 'noanalyze' | 'pending' | 'completed' | 'failed';
      lastAnalysisAt?: Date;
    };
    'ai-predictions'?: {
      status?: 'noanalyze' | 'pending' | 'completed' | 'failed';
      lastAnalysisAt?: Date;
    };
    'ai-recommendations'?: {
      status?: 'noanalyze' | 'pending' | 'completed' | 'failed';
      lastAnalysisAt?: Date;
    };
    'social-media-audience'?: {
      status?: 'noanalyze' | 'pending' | 'completed' | 'failed';
      lastAnalysisAt?: Date;
    };
    'social-media-engagement'?: {
      status?: 'noanalyze' | 'pending' | 'completed' | 'failed';
      lastAnalysisAt?: Date;
    };
    'social-media-analytics'?: {
      status?: 'noanalyze' | 'pending' | 'completed' | 'failed';
      lastAnalysisAt?: Date;
    };
  };
  isActive: boolean;
  preferences?: {
    notifications?: {
      email?: boolean;
      sms?: boolean;
      push?: boolean;
    };
    operations?: {
      period?: 'daily' | 'weekly' | 'monthly';
    };
    lang?: 'tr' | 'en';
  };
  // Server-provided sync metadata
  syncStatus?: 'pending' | 'completed' | 'failed';
  syncError?: string;
  syncAt?: Date;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface Partner {
  _id: string;
  businessName: string;
  email: string;
  phone?: string;
  businessType?: string;
  address?: string;
  website?: string;
  description?: string;
  hours?: string;
  banner?: string;
  avatar?: string;
  bannerSignedUrl?: string;
  avatarSignedUrl?: string;
  rating?: number;
  location?: string;
  distance?: string;
  discount?: string;
  qrCode?: string;
  packageId?: string;
  isParent?: boolean; // Ana partner mi?
  geoLocation?: {
    type: 'Point';
    coordinates: [number, number];
  };
  google?: any;
  googleBusiness?: any;
  facebook?: string;
  instagram?: string;
  twitter?: string;
  reviewCount?: number;
  profileViews?: number;
  isActive?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  preferences?: {
    email?: boolean;
    sms?: boolean;
    weekly?: boolean;
    push?: boolean;
  };
  taskCount?: number;
}

export interface Package {
  _id: string;
  name: string;
  description?: string;
  price: number;
  features: string[];
  isActive: boolean;
  isCustom?: boolean;
  isProduct?: boolean;
  productReferenceCode?: string;
  planReferenceCode?: string;
  paymentInterval?: 'MONTHLY' | 'YEARLY';
  createdAt: string;
  updatedAt: string;
  __v?: number;
}

export interface Subscription {
  _id: string;
  businessId?: string;
  packageId: string;
  iyzicoSubscriptionId?: string;
  referenceCode?: string;
  parentReferenceCode?: string;
  pricingPlanReferenceCode?: string;
  customerReferenceCode?: string;
  subscriptionStatus?: string;
  trialDays?: number;
  createdDate?: number;
  startDate?: number;
  endDate?: number;
  status: 'active' | 'canceled' | 'pending' | 'failed';
  startedAt: string;
  endedAt?: string;
  lastPaymentAt?: string;
  nextPaymentAt?: string;
  rawResponse?: any;
  name?: string;
  surname?: string;
  email?: string;
  gsmNumber?: string;
  identityNumber?: string;
  addressDetails?: {
    city?: string;
    country?: string;
    address?: string;
    zipCode?: string;
  };
  billingAddress?: {
    contactName?: string;
    country?: string;
    city?: string;
    zipCode?: string;
    address?: string;
  };
  shippingAddress?: {
    contactName?: string;
    country?: string;
    city?: string;
    zipCode?: string;
    address?: string;
  };
  createdAt: string;
  updatedAt: string;
}

// Auth
export interface LoginPayload {
  email: string;
  password: string;
}

export interface BusinessLoginPayload {
  email: string;
  password: string;
}

export interface BusinessRegisterPayload {
  name: string;
  email: string;
  password: string;
  phone?: string;
  category: BusinessType;
  location?: {
    address?: string;
    city?: string;
    country?: string;
  };
  website?: string;
  description?: string;
}

export interface RegisterPayload {
  firstName: string;
  lastName: string;
  email: string;
  password?: string;
  phone?: string;
  interests?: string[];
  location?: string;
  businessName?: string;
  businessType?: BusinessType;
  address?: string;
  website?: string;
}

export interface AuthResponse {
  token: string;
  refreshToken?: string;
  user?: User;
  partner?: Partner;
  business?: Business;
}

export interface BusinessAuthResponse {
  message: string;
  business: {
    _id: string;
    name: string;
    email: string;
    emailVerified?: boolean;
    preferences?: any;
    packageId?: string;
  };
  token: string;
  refreshToken: string;
  subscription?: any;
}

export interface TokenVerificationResponse {
  valid: boolean;
  message: string;
  business: {
    _id: string;
    name: string;
    email: string;
    preferences?: any;
    packageId?: string;
    google?: any;
  };
  token: {
    _id: string;
    email: string;
  };
}

export interface Notification {
  _id: string;
  userId: string;
  title: string;
  message: string;
  type: 'system' | 'error' | 'info' | 'warning';
  read: boolean;
  data?: any; // Ek veri (task ID, partner ID vs.)
  createdAt: string;
  updatedAt: string;
} 